import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AdminService, { UserDetails } from '../../services/admin/admin.service';

export const UserDetailsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banDuration, setBanDuration] = useState(7);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    if (!accessToken || !userId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await AdminService.getUserDetails(accessToken, userId);
      setUserDetails(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load user details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBanUser = async () => {
    if (!accessToken || !userId || !banReason) {
      alert('Please provide a ban reason');
      return;
    }

    try {
      await AdminService.banUser(accessToken, userId, banReason, banDuration);
      setShowBanModal(false);
      setBanReason('');
      await fetchUserDetails();
      alert('User banned successfully');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to ban user');
    }
  };

  const handleUnbanUser = async () => {
    if (!accessToken || !userId) return;

    if (!window.confirm('Are you sure you want to unban this user?')) {
      return;
    }

    try {
      await AdminService.unbanUser(accessToken, userId);
      await fetchUserDetails();
      alert('User unbanned successfully');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to unban user');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getRiskColor = (score: number) => {
    if (score >= 70) return '#dc2626';
    if (score >= 40) return '#f59e0b';
    return '#16a34a';
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <button onClick={() => navigate('/admin/users')} style={styles.backButton}>
          ← Back to Users
        </button>
        <p style={styles.loading}>Loading user details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <button onClick={() => navigate('/admin/users')} style={styles.backButton}>
          ← Back to Users
        </button>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!userDetails) return null;

  const { user, riskAssessment, recentSessions, reportsAgainst } = userDetails;

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/admin/users')} style={styles.backButton}>
        ← Back to Users
      </button>

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>User Details</h1>
          <p style={styles.email}>{user.email}</p>
        </div>
        <div style={styles.headerActions}>
          {user.banned ? (
            <button onClick={handleUnbanUser} style={styles.unbanButton}>
              Unban User
            </button>
          ) : (
            <button onClick={() => setShowBanModal(true)} style={styles.banButton}>
              Ban User
            </button>
          )}
        </div>
      </div>

      {/* Ban Status */}
      {user.banned && (
        <div style={styles.banAlert}>
          <span style={styles.banIcon}>🚫</span>
          <div>
            <div style={styles.banTitle}>User is Banned</div>
            <div style={styles.banDetails}>
              Reason: {user.banReason || 'Not specified'}
              {user.banExpiresAt && (
                <> • Expires: {formatDate(user.banExpiresAt)}</>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={styles.grid}>
        {/* User Info Card */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Account Information</h2>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>User ID:</div>
              <div style={styles.infoValue}>{user.id}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Email Verified:</div>
              <div style={styles.infoValue}>{user.isEmailVerified ? '✅ Yes' : '❌ No'}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Premium:</div>
              <div style={styles.infoValue}>{user.isPremium ? '⭐ Yes' : 'No'}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Created:</div>
              <div style={styles.infoValue}>{formatDate(user.createdAt)}</div>
            </div>
            <div style={styles.infoItem}>
              <div style={styles.infoLabel}>Last Login:</div>
              <div style={styles.infoValue}>{formatDate(user.lastLoginAt)}</div>
            </div>
          </div>
        </div>

        {/* Risk Assessment Card */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Risk Assessment</h2>
          <div
            style={{
              ...styles.riskScore,
              backgroundColor: getRiskColor(riskAssessment.riskScore.score),
            }}
          >
            {riskAssessment.riskScore.score}/100
          </div>
          <div style={styles.riskBreakdown}>
            {riskAssessment.riskScore.breakdown.map((factor: string, idx: number) => (
              <div key={idx} style={styles.riskFactor}>
                • {factor}
              </div>
            ))}
          </div>
          <div style={styles.reportCounts}>
            <div style={styles.reportCount}>
              <span style={styles.reportCountValue}>
                {riskAssessment.reportCounts.last24h}
              </span>
              <span style={styles.reportCountLabel}>Reports (24h)</span>
            </div>
            <div style={styles.reportCount}>
              <span style={styles.reportCountValue}>
                {riskAssessment.reportCounts.last7d}
              </span>
              <span style={styles.reportCountLabel}>Reports (7d)</span>
            </div>
            <div style={styles.reportCount}>
              <span style={styles.reportCountValue}>
                {riskAssessment.reportCounts.total}
              </span>
              <span style={styles.reportCountLabel}>Total Reports</span>
            </div>
          </div>
        </div>
      </div>

      {/* User Stats Card */}
      {user.stats && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>User Statistics</h2>
          <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <div style={styles.statValue}>{user.stats.totalSessions || 0}</div>
              <div style={styles.statLabel}>Total Sessions</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statValue}>{user.stats.totalReportsReceived || 0}</div>
              <div style={styles.statLabel}>Reports Received</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statValue}>{user.stats.totalReportsMade || 0}</div>
              <div style={styles.statLabel}>Reports Made</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Sessions */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Recent Sessions ({recentSessions.length})</h2>
        {recentSessions.length === 0 ? (
          <p style={styles.emptyState}>No sessions yet</p>
        ) : (
          <div style={styles.sessionsList}>
            {recentSessions.map((session: any) => (
              <div key={session.id} style={styles.sessionItem}>
                <div style={styles.sessionInfo}>
                  <div style={styles.sessionDate}>{formatDate(session.createdAt)}</div>
                  <div style={styles.sessionDuration}>
                    Duration: {session.duration ? `${session.duration}s` : 'N/A'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reports Against User */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Reports Filed Against User ({reportsAgainst.length})</h2>
        {reportsAgainst.length === 0 ? (
          <p style={styles.emptyState}>No reports</p>
        ) : (
          <div style={styles.reportsList}>
            {reportsAgainst.map((report: any) => (
              <div key={report.id} style={styles.reportItem}>
                <div style={styles.reportReason}>{report.reason}</div>
                <div style={styles.reportDate}>{formatDate(report.createdAt)}</div>
                {report.reviewed && (
                  <div style={styles.reportAction}>{report.action}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ban Modal */}
      {showBanModal && (
        <div style={styles.modalOverlay} onClick={() => setShowBanModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Ban User</h2>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Ban Reason:</label>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  style={styles.textarea}
                  placeholder="Enter ban reason..."
                  rows={3}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Duration (days):</label>
                <input
                  type="number"
                  value={banDuration}
                  onChange={(e) => setBanDuration(parseInt(e.target.value))}
                  style={styles.input}
                  min={1}
                  max={365}
                />
              </div>
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => setShowBanModal(false)} style={styles.cancelButton}>
                Cancel
              </button>
              <button onClick={handleBanUser} style={styles.confirmButton}>
                Ban User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1200px',
  },
  backButton: {
    padding: '0.5rem 1rem',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '1rem',
  },
  loading: {
    fontSize: '1rem',
    color: '#6b7280',
  },
  error: {
    padding: '1rem',
    backgroundColor: '#fee',
    color: '#c33',
    borderRadius: 'var(--radius-md)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 0.5rem',
  },
  email: {
    fontSize: '1.1rem',
    color: '#6b7280',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '1rem',
  },
  banButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  unbanButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#16a34a',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  banAlert: {
    display: 'flex',
    gap: '1rem',
    padding: '1.5rem',
    backgroundColor: '#fee2e2',
    border: '2px solid #dc2626',
    borderRadius: 'var(--radius-lg)',
    marginBottom: '2rem',
  },
  banIcon: {
    fontSize: '2rem',
  },
  banTitle: {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: '#991b1b',
    marginBottom: '0.25rem',
  },
  banDetails: {
    fontSize: '0.95rem',
    color: '#7f1d1d',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem',
    marginBottom: '1.5rem',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginBottom: '1.5rem',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '1rem',
  },
  infoGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  infoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  infoLabel: {
    fontSize: '0.9rem',
    color: '#6b7280',
    fontWeight: '500',
  },
  infoValue: {
    fontSize: '0.9rem',
    color: '#1e293b',
    fontWeight: '600',
    textAlign: 'right',
  },
  riskScore: {
    fontSize: '3rem',
    fontWeight: '700',
    color: 'white',
    textAlign: 'center',
    padding: '1.5rem',
    borderRadius: 'var(--radius-lg)',
    marginBottom: '1rem',
  },
  riskBreakdown: {
    fontSize: '0.9rem',
    color: '#6b7280',
    marginBottom: '1rem',
  },
  riskFactor: {
    padding: '0.25rem 0',
  },
  reportCounts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
    marginTop: '1rem',
  },
  reportCount: {
    textAlign: 'center',
  },
  reportCountValue: {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1e293b',
  },
  reportCountLabel: {
    display: 'block',
    fontSize: '0.8rem',
    color: '#6b7280',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1.5rem',
  },
  statItem: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '0.5rem',
  },
  statLabel: {
    fontSize: '0.9rem',
    color: '#6b7280',
  },
  sessionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  sessionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.875rem',
    backgroundColor: '#f9fafb',
    borderRadius: 'var(--radius-md)',
  },
  sessionInfo: {
    textAlign: 'right',
  },
  sessionDate: {
    fontSize: '0.85rem',
    color: '#6b7280',
  },
  sessionDuration: {
    fontSize: '0.85rem',
    color: '#9ca3af',
  },
  reportsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  reportItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.875rem',
    backgroundColor: '#fef3c7',
    borderRadius: 'var(--radius-md)',
  },
  reportReason: {
    fontWeight: '600',
    color: '#92400e',
  },
  reportDate: {
    fontSize: '0.85rem',
    color: '#78350f',
  },
  reportAction: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#16a34a',
  },
  emptyState: {
    textAlign: 'center',
    color: '#9ca3af',
    padding: '2rem',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    width: '90%',
    maxWidth: '500px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '1.5rem',
  },
  modalBody: {
    marginBottom: '1.5rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.5rem',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e5e7eb',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e5e7eb',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
  },
  modalActions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
};
